import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useNotificationWebSocket } from '@/providers/notification-websocket-provider';
import { useNotificationAPI, NotificationData } from '@/api/notification-service';
import { NotificationPriority } from '@/config/notifications';
import { useOrganization } from '@/contexts/organization-context';

// Extended notification interface that combines backend data with local UI state
export interface Notification extends NotificationData {
  // Backend fields are already included via NotificationData
  // Add any additional UI-specific fields if needed
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Convert backend notification type to legacy UI type for compatibility
export const mapNotificationTypeToUIType = (type: string): 'info' | 'warning' | 'error' | 'success' => {
  switch (type) {
    case 'task_assigned':
      return 'success';
    case 'health_alert':
      return 'error';
    case 'event_change':
      return 'warning';
    case 'event_cancellation':
      return 'error';
    case 'financial':
      return 'info';
    case 'administrative':
      return 'info';
    case 'security':
      return 'error';
    case 'message':
      return 'info';
    case 'announcement':
      return 'info';
    case 'reminder':
      return 'warning';
    // Legacy types for backward compatibility
    case 'health':
      return 'error';
    case 'events':
      return 'warning';
    case 'groups':
      return 'info';
    default:
      return 'info';
  }
};

// Convert priority to UI severity
export const mapPriorityToSeverity = (priority: NotificationPriority): 'info' | 'warning' | 'error' | 'success' => {
  switch (priority) {
    case 'urgent':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
      return 'info';
    default:
      return 'info';
  }
};

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'readAt' | 'userId' | 'organizationId'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  refreshNotifications: () => void;
  clearError: () => void;
  forceReconnect: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  console.log('🚀 NotificationProvider MOUNTED/REMOUNTED');
  
  // Local state for managing notifications and UI state
  const [localNotifications, setLocalNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webSocketHistoryReceived, setWebSocketHistoryReceived] = useState(false);
  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  const [webSocketConnectionAttempted, setWebSocketConnectionAttempted] = useState(false);
  // Track which notifications have been marked as read locally (for optimistic updates)
  const [locallyReadNotifications, setLocallyReadNotifications] = useState<Set<string>>(new Set());

  // Organization context - needed for API calls
  const { currentOrganization, currentInstitution } = useOrganization();

  // Backend integration hooks
  const {
    notifications: webSocketNotifications,
    isConnected,
    markAsRead: wsMarkAsRead,
    markAllAsRead: wsMarkAllAsRead,
    getNotifications: wsGetNotifications,
    forceReconnect: wsForceReconnect,
  } = useNotificationWebSocket();

  const notificationAPI = useNotificationAPI();

  // Helper function to get notification unique identifier
  const getNotificationKey = (notification: Notification | NotificationData): string => {
    // Use the main ID, but also check for originalNotificationId in metadata for deduplication
    const mainId = notification.id;
    const originalId = notification.metadata?.originalNotificationId;
    
    // If there's an originalNotificationId, use that as the base key
    // This helps deduplicate notifications from different channels (email, sms, in_app)
    return originalId || mainId;
  };

  // Merge and deduplicate WebSocket and API notifications
  const mergeNotifications = (): Notification[] => {
    const notificationMap = new Map<string, Notification>();
    
    // First, add API notifications (older, historical data)
    localNotifications.forEach(notification => {
      const key = getNotificationKey(notification);
      notificationMap.set(key, notification);
    });
    
    // Then, add/update with WebSocket notifications (newer, real-time data)
    // WebSocket notifications take precedence over API notifications
    webSocketNotifications.forEach(wsNotification => {
      // Transform WebSocket notification to match frontend Notification interface
      const wsData = wsNotification as any;
      
      const notification: Notification = {
        id: wsData.id,
        type: wsData.type,
        title: wsData.title,
        message: wsData.message,
        priority: wsData.priority,
        userId: wsData.userId || '',
        organizationId: wsData.organizationId || '',
        metadata: wsData.metadata || {},
        // Map WebSocket fields to frontend fields
        createdAt: wsData.timestamp || wsData.createdAt || new Date().toISOString(),
        readAt: wsData.read === true 
          ? (wsData.timestamp || new Date().toISOString()) // If read, use timestamp
          : null, // If not read, set to null
        taskId: wsData.taskId,
      };
      
      const key = getNotificationKey(notification);
      
      // Ensure WebSocket notification has all required fields
      const existingNotification = notificationMap.get(key);
      
      // Smart merge: prioritize LOCAL read status if it exists (user just clicked)
      // This ensures optimistic updates aren't overridden by WebSocket
      const mergedNotification: Notification = {
        ...existingNotification, // Keep any existing data
        ...notification, // Override with WebSocket data
        // IMPORTANT: Check if this notification was marked as read locally
        // If so, preserve the read status even if WebSocket says unread
        readAt: locallyReadNotifications.has(notification.id) 
          ? (existingNotification?.readAt || new Date().toISOString())
          : (existingNotification?.readAt || notification.readAt),
      };
      
      notificationMap.set(key, mergedNotification);
    });
    
    // Convert back to array and sort by creation date (newest first)
    const merged = Array.from(notificationMap.values()).sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
    
    // Debug logging for deduplication
    console.log('NotificationProvider: Merged notifications:', {
      apiCount: localNotifications.length,
      webSocketCount: webSocketNotifications.length,
      mergedCount: merged.length,
      duplicatesRemoved: (localNotifications.length + webSocketNotifications.length) - merged.length
    });
    
    return merged;
  };

  const notifications = mergeNotifications();
  const unreadCount = notifications.filter(n => !n.readAt).length;

  // Track when WebSocket history is received
  useEffect(() => {
    if (webSocketNotifications.length > 0 && !webSocketHistoryReceived) {
      console.log('NotificationProvider: WebSocket history received, setting flag');
      setWebSocketHistoryReceived(true);
      // Stop loading when WebSocket history is received
      setIsLoading(false);
      setError(null);
    }
  }, [webSocketNotifications.length, webSocketHistoryReceived]);

  // Track when WebSocket connection is attempted (connected or failed)
  useEffect(() => {
    if (isConnected || (!isConnected && webSocketConnectionAttempted)) {
      // WebSocket either connected or we've already tried
      return;
    }
    
    // Set a timer to mark WebSocket as "attempted" after giving it a chance to connect
    const timer = setTimeout(() => {
      if (!isConnected) {
        console.log('⏰ WebSocket connection timeout - marking as attempted');
        setWebSocketConnectionAttempted(true);
      }
    }, 3000); // Give WebSocket 3 seconds to connect

    return () => clearTimeout(timer);
  }, [isConnected, webSocketConnectionAttempted]);

  // Mark WebSocket as attempted when it connects
  useEffect(() => {
    if (isConnected && !webSocketConnectionAttempted) {
      console.log('✅ WebSocket connected - marking as attempted');
      setWebSocketConnectionAttempted(true);
    }
  }, [isConnected, webSocketConnectionAttempted]);

  // Handle WebSocket history reception - set loading to false when we receive any WebSocket data
  useEffect(() => {
    if (isConnected && webSocketNotifications !== undefined && !webSocketHistoryReceived) {
      console.log('✅ WebSocket history received, stopping loading state:', webSocketNotifications.length);
      setWebSocketHistoryReceived(true);
      setIsLoading(false);
      setError(null);
    }
  }, [isConnected, webSocketNotifications, webSocketHistoryReceived]);

  // Load initial notifications - ZERO API calls when WebSocket is available
  useEffect(() => {
    console.log('🔄 NotificationProvider useEffect triggered:', {
      orgId: currentOrganization?.id,
      instId: currentInstitution?.id,
      isConnected,
      webSocketHistoryReceived,
      webSocketConnectionAttempted,
      apiCallInProgress
    });

    // ABSOLUTE GUARD: Never make API calls if WebSocket history was received
    if (webSocketHistoryReceived) {
      console.log('✅ GUARD 1: WebSocket history available, blocking all API calls');
      setIsLoading(false);
      return;
    }

    // ABSOLUTE GUARD: Never make API calls if WebSocket is connected (regardless of data received yet)
    if (isConnected) {
      console.log('✅ GUARD 2: WebSocket connected, blocking all API calls and trusting WebSocket completely');
      setIsLoading(true);
      setError(null);
      return;
    }

    // Only proceed if we have organization context (institution is optional for org admins)
    if (!currentOrganization?.id) {
      console.log('⏸️ Missing organization context');
      return;
    }

    // CRITICAL: Only make API call if WebSocket connection has been attempted and failed
    if (!isConnected && webSocketConnectionAttempted && !apiCallInProgress && !webSocketHistoryReceived) {
      console.log('⚠️ WebSocket unavailable after timeout - Making API call as last resort');
      loadFromAPI();
    } else if (!webSocketConnectionAttempted) {
      console.log('⏳ Waiting for WebSocket connection attempt...');
      setIsLoading(true);
    } else {
      console.log('🚫 API call blocked by final check');
    }
  }, [currentOrganization?.id, currentInstitution?.id, isConnected, webSocketHistoryReceived, webSocketConnectionAttempted]);

  // Separate function for API loading (only called when WebSocket is not available)
  const loadFromAPI = async () => {
    // TRIPLE CHECK: Absolutely no API calls if WebSocket is available or history received
    if (webSocketHistoryReceived) {
      console.log('NotificationProvider: BLOCKED API call - WebSocket history available');
      return;
    }
    
    if (isConnected) {
      console.log('NotificationProvider: BLOCKED API call - WebSocket is connected');
      return;
    }
    
    if (apiCallInProgress) {
      console.log('NotificationProvider: BLOCKED API call - Already in progress');
      return;
    }

    console.log('NotificationProvider: ⚠️ Making API call as LAST RESORT (WebSocket unavailable)');
    setApiCallInProgress(true);
    setIsLoading(true);
    setError(null);
    
    try {
      // Final check before actual API call
      if (webSocketHistoryReceived || isConnected) {
        console.log('NotificationProvider: CANCELLED API call - WebSocket became available');
        return;
      }
      
      const response = await notificationAPI.getNotifications(1, 20);
      
      // Final check before updating state
      if (!webSocketHistoryReceived && !isConnected) {
        setLocalNotifications(response.notifications as Notification[]);
        console.log('NotificationProvider: Successfully loaded', response.notifications.length, 'notifications from API');
      } else {
        console.log('NotificationProvider: IGNORED API response - WebSocket data available');
      }
    } catch (err) {
      console.error('NotificationProvider: API call failed:', err);
      setError('Failed to load notification history');
    } finally {
      setIsLoading(false);
      setApiCallInProgress(false);
    }
  };

  // Add notification (for local/manual notifications)
  const addNotification = useCallback((notificationData: Omit<Notification, 'id' | 'createdAt' | 'readAt' | 'userId' | 'organizationId'>) => {
    const newNotification: Notification = {
      ...notificationData,
      id: `local-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      readAt: null, // Initially unread
      userId: 'current-user', // This would come from auth context
      organizationId: currentOrganization?.id || 'unknown-org',
    };

    setLocalNotifications(prev => [newNotification, ...prev]);
  }, [currentOrganization?.id]);

  // Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    const now = new Date().toISOString();
    
    // IMMEDIATELY update UI state for instant feedback
    console.log('🔄 Marking notification as read (optimistic):', id);
    
    // Add to locally read notifications set for tracking
    setLocallyReadNotifications(prev => new Set(prev).add(id));
    
    // Update local notifications optimistically
    setLocalNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, readAt: now } : n))
    );

    try {
      if (isConnected) {
        // Use WebSocket for real-time sync
        console.log('📡 Sending WebSocket markAsRead:', id);
        wsMarkAsRead(id);
      } else {
        // Fall back to API if WebSocket is not connected
        console.log('🌐 Using API markAsRead:', id);
        const response = await notificationAPI.markAsRead(id);
        // Update with server timestamp if different
        setLocalNotifications(prev =>
          prev.map(n => (n.id === id ? { ...n, readAt: response.readAt } : n))
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // State is already updated optimistically, so no additional action needed
    }
  }, [isConnected, wsMarkAsRead, notificationAPI]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const now = new Date().toISOString();
    
    // IMMEDIATELY update UI state for instant feedback
    console.log('🔄 Marking all notifications as read (optimistic)');
    
    // Update local notifications optimistically
    setLocalNotifications(prev =>
      prev.map(n => ({ ...n, readAt: n.readAt || now }))
    );

    try {
      if (isConnected) {
        // Use WebSocket for real-time sync
        console.log('📡 Sending WebSocket markAllAsRead');
        wsMarkAllAsRead();
      } else {
        // Fall back to API if WebSocket is not connected
        console.log('🌐 Using API markAllAsRead');
        await notificationAPI.markAllAsRead();
        // State is already updated optimistically
      }
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      // State is already updated optimistically, so no additional action needed
    }
  }, [isConnected, wsMarkAllAsRead, notificationAPI]);

  // Remove notification (local only)
  const removeNotification = useCallback((id: string) => {
    setLocalNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Refresh notifications (prioritize WebSocket, fallback to API)
  const refreshNotifications = useCallback(async () => {
    // Prevent multiple simultaneous refresh calls
    if (apiCallInProgress) {
      console.log('NotificationProvider: Refresh already in progress, skipping');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // If WebSocket is connected, prioritize WebSocket history
      if (isConnected) {
        console.log('NotificationProvider: Refreshing from WebSocket...');
        wsGetNotifications(20, 0);
        // Reset WebSocket history flag to allow re-receiving
        setWebSocketHistoryReceived(false);
        setApiCallInProgress(false); // Reset API call flag
      } else {
        // Fall back to API if WebSocket is not connected
        if (!apiCallInProgress) {
          console.log('NotificationProvider: Refreshing from API (WebSocket not connected)...');
          setApiCallInProgress(true);
          const response = await notificationAPI.getNotifications(1, 20);
          setLocalNotifications(response.notifications as Notification[]);
          setApiCallInProgress(false);
        }
      }
    } catch (err) {
      console.error('Failed to refresh notifications:', err);
      setError('Failed to refresh notifications');
      setApiCallInProgress(false);
      
      // If WebSocket refresh failed, try API as fallback
      if (isConnected && !apiCallInProgress) {
        try {
          console.log('NotificationProvider: WebSocket refresh failed, trying API...');
          setApiCallInProgress(true);
          const response = await notificationAPI.getNotifications(1, 20);
          setLocalNotifications(response.notifications as Notification[]);
        } catch (apiErr) {
          console.error('API fallback also failed:', apiErr);
        } finally {
          setApiCallInProgress(false);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, wsGetNotifications, notificationAPI, apiCallInProgress]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Force reconnect - resets connection and fetches fresh data
  const forceReconnect = useCallback(async () => {
    console.log('NotificationProvider: Force reconnecting...');
    setIsLoading(true);
    setError(null);
    setWebSocketHistoryReceived(false);
    setApiCallInProgress(false);
    setLocalNotifications([]);
    
    try {
      await wsForceReconnect();
    } catch (err) {
      console.error('NotificationProvider: Force reconnect failed:', err);
      setError('Failed to reconnect');
    } finally {
      setIsLoading(false);
    }
  }, [wsForceReconnect]);

  const contextValue: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    error,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    refreshNotifications,
    clearError,
    forceReconnect,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
} 